import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { X, Sparkles } from "lucide-react";
import TemplateDetailModal from "./TemplateDetailModal";
import AIRecommendationModal from "./AIRecommendationModal";
import ApproveHealthResultModal from "./ApproveHealthResultModal";
import { templateAPI, healthRecommendationAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background-color: #FFFFFF;
  border-radius: 1rem;
  padding: 0;
  width: 95vw;
  max-width: 95vw;
  height: 90vh;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.95)'};
  transition: transform 0.3s ease;
  font-family: 'Prompt', sans-serif;
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ECECEC;
`;

const ModalTitle = styled.h2`
  color: #000000;
  font-size: 25px;
  font-weight: 400;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  svg {
    color: #CFD3D4;
    width: 14px;
    height: 14px;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const PatientInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 3fr;
  gap: 1.5rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #ECECEC;
`;

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoItem = styled.div`
  font-size: 14px;
  color: #6F6F6F;
`;

const InfoLabel = styled.span`
  font-weight: 700;
`;

const InfoValue = styled.span`
  font-weight: 400;
`;

const HNValue = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #6F6F6F;
`;

const ParameterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ParameterTable = styled.div`
  display: grid;
  grid-template-columns: 150px 150px 120px 80px;
  gap: 0.5rem;
  margin-top: 1rem;
  justify-content: center;
`;

const ParameterTableHeader = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #6F6F6F;
  text-align: center;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #ECECEC;
`;

const ParameterTableCell = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #6F6F6F;
  text-align: center;
  padding: 0.25rem 0;
`;

const ComparisonBadge = styled.div`
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
  background-color: transparent;
  color: ${props => {
    if (props.status === 'high') return '#DC2626';
    if (props.status === 'low') return '#D97706';
    return '#059669';
  }};
`;

const ParameterHeader = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #6F6F6F;
  margin-bottom: 0.5rem;
`;

const ParameterItem = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #6F6F6F;
`;

const TemplateTag = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: transparent;
  border: 1px solid #00B5B8;
  border-radius: 0px;
  padding: 4px 8px;
  margin: 2px;
  font-size: 14px;
  color: #00B5B8;
  cursor: pointer;
  position: relative;
  width: fit-content;

  &:hover {
    background-color: #00B5B8;
    color: white;
  }
`;

const RecommendationSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #ECECEC;
`;

const RecommendationColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RecommendationTitle = styled.h3`
  font-size: 16px;
  font-weight: 400;
  color: #000000;
  margin: 0 0 1rem 0;
`;

const ImageSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const RecommendationImage = styled.div`
  width: 58px;
  height: 58px;
  background-color: #F3F4F6;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
  font-size: 12px;
`;

const EditImageButton = styled.button`
  background: none;
  border: 2px dashed #D1D5DB;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 12px;
  color: #6B7280;
  transition: all 0.2s;
  position: relative;

  &:hover {
    border-color: #00B5B8;
    color: #00B5B8;
  }
`;

const HiddenFileInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

const RecommendationInput = styled.textarea`
  width: 270px;
  height: ${props => props.height || '200px'};
  padding: 0.75rem;
  border: 1px solid #E5E7EB;
  border-radius: 0.5rem;
  font-size: 14px;
  color: #111827;
  resize: none;
  font-family: 'Prompt', sans-serif;

  &:focus {
    outline: none;
    border-color: #00B5B8;
  }
`;

const AIText = styled.div`
  font-size: 14px;
  color: #0F172A;
  margin-top: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: #06B6D4;
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    color: #06B6D4;
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem;
  display: flex;
  justify-content: center;
  gap: 1rem;
`;

const CancelButton = styled.button`
  background-color: #F8F8F8;
  color: #6F6F6F;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 2rem;
  font-size: 14px;
  font-weight: 400;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    background-color: ${props => props.disabled ? '#F8F8F8' : '#E5E7EB'};
  }
`;

const SubmitButton = styled.button`
  background-color: ${props => props.disabled ? '#CFD3D4' : '#00B5B8'};
  color: #FFFFFF;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 2rem;
  font-size: 14px;
  font-weight: 400;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.disabled ? '#CFD3D4' : '#0891B2'};
  }
`;

const ResendButton = styled.button`
  background-color: ${props => props.disabled ? '#CFD3D4' : '#1D87F0'};
  color: #FFFFFF;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 2rem;
  font-size: 14px;
  font-weight: 400;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.disabled ? '#CFD3D4' : '#1565C0'};
  }
`;

const HealthResultModal = ({ isOpen, onClose, selectedRecord, onApprove }) => {
  const { getUserFullName } = useAuth();
  const [recommendations, setRecommendations] = useState({
    doctor: '',
    food: '',
    exercise: '',
    sleep: ''
  });
  const [images, setImages] = useState({
    food: 'food.jpg',
    exercise: 'exercise.jpg',
    sleep: 'sleep.jpg'
  });
  const [isTemplateDetailModalOpen, setIsTemplateDetailModalOpen] = useState(false);
  const [selectedTemplateForDetail, setSelectedTemplateForDetail] = useState(null);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [isApproveConfirmModalOpen, setIsApproveConfirmModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState('pending'); // เปลี่ยนเป็น state แทน

  // ฟังก์ชันเปรียบเทียบค่าปกติกับค่าที่ตรวจวัดได้
  const compareValues = (normalRange, resultValue) => {
    if (!normalRange || !resultValue || normalRange === 'xxx' || resultValue === 'xxx' || normalRange === '-' || resultValue === '-') {
      return { status: 'normal', label: '-' };
    }

    const result = parseFloat(resultValue);
    if (isNaN(result)) {
      return { status: 'normal', label: '-' };
    }

    if (normalRange.includes('-')) {
      const [min, max] = normalRange.split('-').map(v => parseFloat(v.trim()));
      if (result < min) {
        return { status: 'low', label: 'Low' };
      } else if (result > max) {
        return { status: 'high', label: 'High' };
      } else {
        return { status: 'normal', label: 'Normal' };
      }
    } else if (normalRange.startsWith('<')) {
      const max = parseFloat(normalRange.replace('<', '').trim());
      if (result >= max) {
        return { status: 'high', label: 'High' };
      } else {
        return { status: 'normal', label: 'Normal' };
      }
    } else if (normalRange.startsWith('>')) {
      const min = parseFloat(normalRange.replace('>', '').trim());
      if (result <= min) {
        return { status: 'low', label: 'Low' };
      } else {
        return { status: 'normal', label: 'Normal' };
      }
    }

    return { status: 'normal', label: 'Normal' };
  };

  // โหลด draft และ approval_status เมื่อเปิด modal
  useEffect(() => {
    if (isOpen && selectedRecord?.id) {
      console.log('🔍 DEBUG - selectedRecord:', selectedRecord);
      console.log('🔍 DEBUG - templates:', selectedRecord.templates);
      console.log('🔍 DEBUG - matchedTemplates:', selectedRecord.matchedTemplates);
      loadDraftAndStatus();
    }
  }, [isOpen, selectedRecord?.id]);

  // ฟังก์ชันดึง draft และ approval_status ล่าสุดจาก DB
  const loadDraftAndStatus = async () => {
    try {
      const response = await healthRecommendationAPI.getDraft(selectedRecord.id);
      if (response.status === 'success' && response.data) {
        // อัปเดต recommendations
        setRecommendations({
          doctor: response.data.doctor_advice || '',
          food: response.data.nutrition_advice || '',
          exercise: response.data.exercise_advice || '',
          sleep: response.data.sleep_advice || ''
        });

        // อัปเดต approval_status จาก DB
        const latestStatus = response.data.approval_status || 'pending';
        setApprovalStatus(latestStatus);
        console.log('✅ Loaded latest approval status:', latestStatus);
      } else {
        // ถ้าไม่มี draft ให้ clear recommendations และใช้สถานะ pending
        setRecommendations({
          doctor: '',
          food: '',
          exercise: '',
          sleep: ''
        });
        setApprovalStatus('pending');
        console.log('✅ No draft found, cleared recommendations');
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      // ถ้า error ให้ clear recommendations และใช้สถานะ pending
      setRecommendations({
        doctor: '',
        food: '',
        exercise: '',
        sleep: ''
      });
      setApprovalStatus('pending');
    }
  };

  if (!selectedRecord) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose && onClose();
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const recommendationsData = {
        doctor_advice: recommendations.doctor,
        nutrition_advice: recommendations.food,
        exercise_advice: recommendations.exercise,
        sleep_advice: recommendations.sleep
      };

      const response = await healthRecommendationAPI.saveDraft(
        selectedRecord.id,
        recommendationsData,
        'admin'
      );

      if (response.status === 'success') {
        // บันทึกสำเร็จ - ปิด modal อัตโนมัติ
        onClose && onClose();
      } else {
        console.error('Save failed:', response.message);
      }
    } catch (error) {
      console.error('Error saving recommendations:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleApproveClick = () => {
    // เปิด confirmation modal
    setIsApproveConfirmModalOpen(true);
  };

  const handleApproveConfirm = async () => {
    setIsSaving(true);
    try {
      const recommendationsData = {
        doctor_advice: recommendations.doctor,
        nutrition_advice: recommendations.food,
        exercise_advice: recommendations.exercise,
        sleep_advice: recommendations.sleep
      };

      const response = await healthRecommendationAPI.approveAndSend(
        selectedRecord.id,
        recommendationsData,
        getUserFullName(), // approved_by - ใช้ชื่อจริงของ user ที่ login
        selectedRecord.templates || [] // templates
      );

      if (response.status === 'success') {
        // อัปเดตสถานะเป็น 'approved' ทันที
        setApprovalStatus('approved');
        console.log('✅ Approval status updated to: approved');

        // อัพเดทสถานะของ record ใน parent component (healthbook จะ reload ข้อมูลใหม่)
        if (onApprove) {
          onApprove(selectedRecord.id);
        }

        // ปิด modal
        onClose && onClose();
      } else {
        console.error('Approve failed:', response.message);
      }
    } catch (error) {
      console.error('Error approving recommendations:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseApproveConfirmModal = () => {
    setIsApproveConfirmModalOpen(false);
  };

  const handleRecommendationChange = (field, value) => {
    setRecommendations(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (field, event) => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setImages(prev => ({
        ...prev,
        [field]: imageUrl
      }));
    }
  };

  const handleTemplateClick = async (templateName) => {
    try {
      // ค้นหา template จาก API
      const response = await templateAPI.getAll();
      if (response.status === 'success' && response.data) {
        const template = response.data.find(t => t.template_name === templateName);

        if (template) {
          // แปลงข้อมูลให้ตรงกับรูปแบบที่ TemplateDetailModal ต้องการ
          const formattedTemplate = {
            id: template.id,
            templateName: template.template_name,
            createdBy: template.created_by,
            tags: template.tags || [],
            gender: template.gender === 'male' ? 'ชาย' : template.gender === 'female' ? 'หญิง' : 'ทั้งสองเพศ',
            ageRange: `${template.age_min}-${template.age_max}`,
            parameters: template.parameters?.map(p => p.parameter_name) || [],
            normalValues: template.parameters?.map(p => p.normal_range || '-') || [],
            setValues: template.parameters?.map(p => `${p.custom_min}-${p.custom_max}`) || []
          };

          setSelectedTemplateForDetail(formattedTemplate);
          setIsTemplateDetailModalOpen(true);
        }
      }
    } catch (error) {
      console.error('Error loading template:', error);
    }
  };

  const handleCloseTemplateDetailModal = () => {
    setIsTemplateDetailModalOpen(false);
    setSelectedTemplateForDetail(null);
  };

  const handleAIRecommendation = () => {
    setIsAIModalOpen(true);
  };

  const handleCloseAIModal = () => {
    setIsAIModalOpen(false);
  };

  const handleAIFinish = (aiData) => {
    // กรอกข้อมูล AI recommendations ลงในช่อง input ต่างๆ
    console.log('Received AI data:', aiData);
    setRecommendations(prev => ({
      ...prev,
      doctor: aiData.doctor_advice || aiData.doctorAdvice || '',
      food: aiData.nutrition_advice || aiData.dietAdvice || '',
      exercise: aiData.exercise_advice || aiData.exerciseAdvice || '',
      sleep: aiData.sleep_advice || aiData.sleepAdvice || ''
    }));
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent isOpen={isOpen}>
        <ModalHeader>
          <div>
            <ModalTitle>
              ยืนยันการส่งผลสุขภาพ
            </ModalTitle>
            <div style={{ fontSize: '14px', color: '#6F6F6F', marginTop: '0.5rem' }}>
              สถานะ: {
                approvalStatus === 'approved' ? 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว' :
                approvalStatus === 'revised' ? 'มีการแก้ไขรอการอนุมัติอีกครั้ง' :
                'รอการอนุมัติ'
              }
            </div>
          </div>
          <CloseButton onClick={onClose}>
            <X />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {/* Patient Information - 4 Columns */}
          <PatientInfoGrid>
            <InfoColumn>
              <InfoItem>
                <InfoLabel>ชื่อ:</InfoLabel> <InfoValue>{selectedRecord.patientName}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>เพศ:</InfoLabel> <InfoValue>{selectedRecord.gender}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>อายุ:</InfoLabel> <InfoValue>{selectedRecord.age}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>โรคประจำตัว:</InfoLabel> <InfoValue>{selectedRecord.diseases}</InfoValue>
              </InfoItem>
            </InfoColumn>

            <InfoColumn>
              <InfoItem style={{ fontWeight: '400' }}>HN</InfoItem>
              <HNValue>{selectedRecord.hn}</HNValue>
            </InfoColumn>

            <ParameterColumn>
              <ParameterTable>
                <ParameterTableHeader>พารามิเตอร์</ParameterTableHeader>
                <ParameterTableHeader>ค่าปกติ</ParameterTableHeader>
                <ParameterTableHeader>ค่าที่ตรวจวัดได้</ParameterTableHeader>
                <ParameterTableHeader></ParameterTableHeader>

                {selectedRecord.parameters.map((param, index) => {
                  const comparison = compareValues(param.normal, param.result);
                  return (
                    <React.Fragment key={index}>
                      <ParameterTableCell>{param.name}</ParameterTableCell>
                      <ParameterTableCell>{param.normal}</ParameterTableCell>
                      <ParameterTableCell>{param.result}</ParameterTableCell>
                      <ParameterTableCell>
                        <ComparisonBadge status={comparison.status}>
                          {comparison.label}
                        </ComparisonBadge>
                      </ParameterTableCell>
                    </React.Fragment>
                  );
                })}
              </ParameterTable>
            </ParameterColumn>

            <InfoColumn>
              <ParameterHeader>การแปลผลสุขภาพที่กำหนดไว้</ParameterHeader>
              <div>
                {selectedRecord.templates && selectedRecord.templates.length > 0 ? (
                  selectedRecord.templates.map((template, index) => (
                    <TemplateTag
                      key={index}
                      onClick={() => handleTemplateClick(template)}
                    >
                      {template}
                    </TemplateTag>
                  ))
                ) : (
                  <div style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
                    ไม่มี template ที่ match หรือยังไม่ได้เลือก
                  </div>
                )}
              </div>
            </InfoColumn>
          </PatientInfoGrid>

          {/* Recommendations - 4 Columns */}
          <RecommendationSection>
            <RecommendationColumn>
              <RecommendationTitle>คำแนะนำสุขภาพโดยหมอ</RecommendationTitle>
              <RecommendationInput
                height="280px"
                value={recommendations.doctor}
                onChange={(e) => handleRecommendationChange('doctor', e.target.value)}
                
              />
            </RecommendationColumn>

            <RecommendationColumn>
              <RecommendationTitle>แนะนำการรับประทานอาหาร</RecommendationTitle>
              <ImageSection>
                <RecommendationImage
                  style={{
                    backgroundImage: images.food ? `url(${images.food})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {!images.food && 'รูป'}
                </RecommendationImage>
                <EditImageButton>
                  แก้ไขรูป
                  <HiddenFileInput
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('food', e)}
                  />
                </EditImageButton>
              </ImageSection>
              <RecommendationInput
                value={recommendations.food}
                onChange={(e) => handleRecommendationChange('food', e.target.value)}
                
              />
            </RecommendationColumn>

            <RecommendationColumn>
              <RecommendationTitle>แนะนำการออกกำลังกาย</RecommendationTitle>
              <ImageSection>
                <RecommendationImage
                  style={{
                    backgroundImage: images.exercise ? `url(${images.exercise})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {!images.exercise && 'รูป'}
                </RecommendationImage>
                <EditImageButton>
                  แก้ไขรูป
                  <HiddenFileInput
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('exercise', e)}
                  />
                </EditImageButton>
              </ImageSection>
              <RecommendationInput
                value={recommendations.exercise}
                onChange={(e) => handleRecommendationChange('exercise', e.target.value)}
                
              />
            </RecommendationColumn>

            <RecommendationColumn>
              <RecommendationTitle>แนะนำการนอน</RecommendationTitle>
              <ImageSection>
                <RecommendationImage
                  style={{
                    backgroundImage: images.sleep ? `url(${images.sleep})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {!images.sleep && 'รูป'}
                </RecommendationImage>
                <EditImageButton>
                  แก้ไขรูป
                  <HiddenFileInput
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('sleep', e)}
                  />
                </EditImageButton>
              </ImageSection>
              <RecommendationInput
                value={recommendations.sleep}
                onChange={(e) => handleRecommendationChange('sleep', e.target.value)}
                
              />
              <AIText onClick={handleAIRecommendation}>
                รับคำแนะนำจากโปรแกรมอัตโนมัติ
                <Sparkles />
              </AIText>
            </RecommendationColumn>
          </RecommendationSection>
        </ModalBody>

        <ModalFooter>
          <CancelButton onClick={onClose} disabled={isSaving}>
            ยกเลิก
          </CancelButton>
          <SubmitButton onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? 'กำลังบันทึก...' : 'บันทึก'}
          </SubmitButton>
          {approvalStatus === 'approved' ? (
            <ResendButton onClick={handleApproveClick} disabled={isSaving}>
              {isSaving ? 'กำลังส่ง...' : 'ส่งใหม่'}
            </ResendButton>
          ) : (
            <SubmitButton onClick={handleApproveClick} disabled={isSaving}>
              {isSaving ? 'กำลังอนุมัติ...' : 'อนุมัติและจัดส่งผลการตรวจสุขภาพ'}
            </SubmitButton>
          )}
        </ModalFooter>
      </ModalContent>

      <TemplateDetailModal
        isOpen={isTemplateDetailModalOpen}
        onClose={handleCloseTemplateDetailModal}
        templateData={selectedTemplateForDetail}
      />

      <AIRecommendationModal
        isOpen={isAIModalOpen}
        onClose={handleCloseAIModal}
        patientData={selectedRecord}
        onFinish={handleAIFinish}
      />

      <ApproveHealthResultModal
        isOpen={isApproveConfirmModalOpen}
        onClose={handleCloseApproveConfirmModal}
        onConfirm={handleApproveConfirm}
      />
    </ModalOverlay>
  );
};

export default HealthResultModal;